/**
 * §2.4 — "An admin UI lists people, their role, status, and last-seen date."
 *
 * All four of those are facts about an account, which is why this is Users
 * rather than People: the directory next door lists everybody we have a record
 * of, and this lists the subset who can sign in. Somebody with no account is
 * not a row hidden here by policy — they are not in the set.
 *
 * Two writes live on this screen and nowhere else. **Changing a role** moves
 * the person's entire §4.4 baseline everywhere at once without touching a grant
 * row, which is why it carries a warning rather than being an inline dropdown
 * that saves on blur. **Deactivating** is a flow with its own page, its own
 * capability, and blockers to clear first (§2.6).
 *
 * Giving somebody access is not here. It starts from a person — you are looking
 * at a record and deciding they need a login — so it lives on their page in the
 * directory, which is also the only place the person to give it to is already
 * chosen.
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { Form } from 'react-aria-components';
import { useMe, useResendInvitation, useUpdateUser, useUsers } from '../../../../api/queries.ts';
import {
  type Person,
  USER_ROLES,
  USER_STATUSES,
  type UserRole,
  type UserStatus,
} from '../../../../api/types.ts';
import { roleCarries } from '../../../../components/capabilities.ts';
import { formatRelative } from '../../../../components/format.ts';
import { Badge, RoleBadge, roleLabel, StatusBadge } from '../../../../components/ui/badge.tsx';
import { Button, LinkButton } from '../../../../components/ui/button.tsx';
import { Dialog, DialogBody, DialogFooter } from '../../../../components/ui/dialog.tsx';
import { Alert, ErrorState } from '../../../../components/ui/feedback.tsx';
import { Select, TextField } from '../../../../components/ui/field.tsx';
import { Card, PageHeader } from '../../../../components/ui/layout.tsx';
import { Table, TableEmpty, TBody, TD, TH, THead, TR } from '../../../../components/ui/table.tsx';

const STATUS_OPTIONS = [
  { value: 'all' as const, label: 'Any status' },
  ...USER_STATUSES.map((status) => ({ value: status, label: status })),
];

const ROLE_FILTER_OPTIONS = [
  { value: 'all' as const, label: 'Any role' },
  ...USER_ROLES.map((role) => ({ value: role, label: roleLabel(role) })),
];

const ROLE_OPTIONS = USER_ROLES.map((role) => ({ value: role, label: roleLabel(role) }));

function ChangeRoleDialog({ user, onClose }: { user: Person; onClose: () => void }) {
  const current = user.account?.role;
  const update = useUpdateUser(user.id);
  const [role, setRole] = useState<UserRole | undefined>(current);

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        if (role !== undefined) update.mutate({ role }, { onSuccess: onClose });
      }}
    >
      <DialogBody>
        <Select
          label="Role"
          options={ROLE_OPTIONS}
          selectedKey={role}
          onSelectionChange={setRole}
        />
        {role !== current ? (
          <Alert tone="warning" title="A role change moves their whole baseline">
            Role capabilities apply everywhere this person already has a grant reaching, so this
            takes effect across every space at once without a single grant row changing. Check the
            effective-access view afterwards.
          </Alert>
        ) : null}
        {update.isError ? <ErrorState error={update.error} /> : null}
      </DialogBody>
      <DialogFooter>
        <Button variant="tertiary" onPress={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          isPending={update.isPending}
          isDisabled={role === undefined || role === current}
        >
          Change role
        </Button>
      </DialogFooter>
    </Form>
  );
}

/** Invited and not yet arrived is the one state with an action attached. */
function ResendButton({ personId }: { personId: string }) {
  const resend = useResendInvitation(personId);
  return (
    <div className="flex items-center justify-end gap-2">
      <Button
        size="sm"
        variant="secondary"
        isPending={resend.isPending}
        onPress={() => resend.mutate()}
      >
        Resend
      </Button>
      {resend.isSuccess ? (
        <span className="text-xs text-success-primary">
          {resend.data.invitation.sent ? 'Sent.' : `Not sent: ${resend.data.invitation.error}`}
        </span>
      ) : null}
    </div>
  );
}

export function UserListPage() {
  const search = useSearch({ from: '/_authed/_employee/settings/workspace/users/' });
  const navigate = useNavigate();
  const [changing, setChanging] = useState<Person | null>(null);

  const me = useMe();
  const canDeactivate = me.data ? roleCarries(me.data.user.role, 'user.deactivate') : false;

  const users = useUsers({
    ...(search.status && search.status !== 'none' ? { status: search.status } : {}),
    ...(search.role ? { role: search.role } : {}),
    ...(search.q ? { q: search.q } : {}),
  });

  const setFilter = (patch: Record<string, string | undefined>) => {
    void navigate({
      to: '/settings/workspace/users',
      search: { ...search, ...patch },
      replace: true,
    });
  };

  return (
    <>
      <PageHeader
        title="Users"
        description="Everyone who can sign in, and how. Staff and contractors authenticate through Google Workspace; portal members use a magic link. Neither path can create an account — access is given from a person's record in People."
      />

      <Card padded={false}>
        <div className="flex flex-col gap-3 border-b border-secondary p-4 sm:flex-row sm:items-end">
          <TextField
            label="Search"
            type="search"
            className="sm:max-w-xs"
            value={search.q ?? ''}
            onChange={(value) => setFilter({ q: value || undefined })}
            placeholder="Name or email"
          />
          <Select
            label="Status"
            className="sm:w-44"
            options={STATUS_OPTIONS}
            selectedKey={search.status ?? 'all'}
            onSelectionChange={(value) =>
              setFilter({ status: value === 'all' ? undefined : (value as UserStatus) })
            }
          />
          <Select
            label="Role"
            className="sm:w-48"
            options={ROLE_FILTER_OPTIONS}
            selectedKey={search.role ?? 'all'}
            onSelectionChange={(value) =>
              setFilter({ role: value === 'all' ? undefined : (value as UserRole) })
            }
          />
        </div>

        {users.isError ? (
          <div className="p-4">
            <ErrorState error={users.error} />
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Role</TH>
                <TH>Status</TH>
                <TH>Sign-in</TH>
                <TH>Last seen</TH>
                <TH className="text-right">Actions</TH>
              </TR>
            </THead>
            <TBody>
              {users.isPending ? (
                <TableEmpty colSpan={6}>Loading…</TableEmpty>
              ) : users.data.users.length === 0 ? (
                <TableEmpty colSpan={6}>Nobody matches those filters.</TableEmpty>
              ) : (
                users.data.users.map((user) => (
                  <TR key={user.id} className="hover:bg-secondary">
                    <TD>
                      <Link
                        to="/people/$personId"
                        params={{ personId: user.id }}
                        className="font-medium text-primary no-underline hover:underline"
                      >
                        {user.name}
                      </Link>
                      <span className="block text-xs text-tertiary">{user.email}</span>
                    </TD>
                    <TD>{user.account ? <RoleBadge role={user.account.role} /> : null}</TD>
                    <TD>{user.account ? <StatusBadge status={user.account.status} /> : null}</TD>
                    <TD>
                      {user.account ? (
                        <Badge tone="outline" mono>
                          {user.account.authMethod}
                        </Badge>
                      ) : null}
                    </TD>
                    <TD className="whitespace-nowrap">
                      {formatRelative(user.account?.lastSeenAt ?? null)}
                    </TD>
                    <TD className="text-right">
                      {user.account?.status === 'invited' ? (
                        <ResendButton personId={user.id} />
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <Button size="sm" variant="secondary" onPress={() => setChanging(user)}>
                            Change role
                          </Button>
                          {canDeactivate && user.account?.status !== 'deactivated' ? (
                            <LinkButton
                              size="sm"
                              variant="destructive-secondary"
                              href={`/settings/workspace/users/${user.id}/deactivate`}
                            >
                              Deactivate
                            </LinkButton>
                          ) : null}
                        </div>
                      )}
                    </TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        )}
      </Card>

      <Dialog
        title="Change role"
        description="Sets the capability baseline. It grants nothing anywhere on its own — a grant still has to reach the node."
        isOpen={changing !== null}
        onOpenChange={(open) => {
          if (!open) setChanging(null);
        }}
      >
        {(close) =>
          changing ? (
            <ChangeRoleDialog
              user={changing}
              onClose={() => {
                close();
                setChanging(null);
              }}
            />
          ) : null
        }
      </Dialog>
    </>
  );
}
